<!-- dynamic-form.component.html -->
<mat-card class="form-card" [class.kiosk-mode]="kioskMode">
  <!-- Card Header -->
  <mat-card-header *ngIf="formTemplate && formTemplate.metadata">
    <mat-card-title>{{ formTemplate.metadata.form_title }}</mat-card-title>
  </mat-card-header>

  <!-- Loading Spinner -->
  <div class="loading-container" *ngIf="loading">
    <mat-spinner [diameter]="50" [color]="'primary'"></mat-spinner>
  </div>

  <!-- Form Content -->
  <ng-container *ngIf="!loading">
    <!-- NOTE: We add (selectionChange)="onStepSelectionChange($event)" here -->
    <mat-stepper
      [linear]="isLinear"
      [orientation]="(stepperOrientation | async)!"
      #stepper
      (selectionChange)="onStepSelectionChange($event)"
    >
      <!-- ======================= STEPS (PAGES) ======================= -->
      <ng-container *ngFor="let page of layout.pages; let i = index">
        <!--
           We bind [completed]="stepIsSaved[i]" to indicate if this step
           can be considered 'done' in a linear stepper.
        -->
        <mat-step
          [stepControl]="formPages[page.title]"
          [completed]="stepIsSaved[i]"
        >
          <form [formGroup]="formPages[page.title]">
            <ng-template matStepLabel>{{ page.title }}</ng-template>

            <div fxLayout="row wrap" fxLayoutGap="16px">
              <ng-container *ngFor="let sectionId of page.sections">
                <div fxFlex="100" class="section-title">
                  <h3>{{ formConfig[sectionId]?.metadata?.title }}</h3>
                  <p>{{ formConfig[sectionId]?.metadata?.description }}</p>
                </div>
                <div [formGroupName]="sectionId">
                  <div
                    *ngFor="let field of formConfig[sectionId]?.fields"
                    class="input-field-wrapper"
                    fxFlex="100" fxFlex.gt-sm="48" fxFlex.gt-md="32"
                  >
                    <!-- Address Autocomplete Field -->
                    <ng-container *ngIf="field.name === 'address'">
                      <app-address-auto-complete
                        (addressSelected)="handleAddressChange($event)"
                      ></app-address-auto-complete>
                    </ng-container>

                    <!-- Other Form Fields -->
                    <mat-form-field appearance="outline" *ngIf="field.type !== 'checkbox' && field.type !== 'checkbox-group' && field.type !== 'signature' && field.type !== 'static_text' && field.type !== 'file'">
                      <mat-label>{{ field.label }}</mat-label>

                      <!-- SSN Field with Auto-Dash Directive -->
                      <input
                        matInput
                        *ngIf="field.type === 'text' && field.name === 'ssn'"
                        [formControlName]="field.name"
                        [type]="field.type"
                        [placeholder]="field.placeholder"
                        autoDashSSNFormat
                      >

                      <!-- Other Text Fields -->
                      <input
                        matInput
                        *ngIf="field.type === 'text' && field.name !== 'ssn'"
                        [formControlName]="field.name"
                        [type]="field.type"
                        [placeholder]="field.placeholder"
                      >

                      <!-- Date Field -->
                      <input
                        matInput
                        *ngIf="field.type === 'date'"
                        [formControlName]="field.name"
                        [matDatepicker]="picker"
                      >
                      <mat-datepicker-toggle matSuffix *ngIf="field.type === 'date'" [for]="picker"></mat-datepicker-toggle>
                      <mat-datepicker #picker></mat-datepicker>

                      <!-- Email Field -->
                      <input
                        matInput
                        *ngIf="field.type === 'email'"
                        [formControlName]="field.name"
                      >

                      <!-- Textarea Field -->
                      <textarea
                        matInput
                        *ngIf="field.type === 'textarea'"
                        [formControlName]="field.name"
                      ></textarea>

                      <!-- Select Field -->
                      <mat-select
                        *ngIf="field.type === 'select'"
                        [formControlName]="field.name"
                      >
                        <mat-option *ngFor="let option of field.options" [value]="option">{{ option }}</mat-option>
                      </mat-select>

                      <!-- Error Messages -->
                      <mat-error *ngIf="formPages[page.title].get(sectionId)?.get(field.name)?.hasError('required')">
                        {{ field.label }} is required
                      </mat-error>
                      <mat-error *ngIf="formPages[page.title].get(sectionId)?.get(field.name)?.hasError('pattern')">
                        Invalid {{ field.label }}
                      </mat-error>
                      <mat-error *ngIf="formPages[page.title].get(sectionId)?.get(field.name)?.hasError('email')">
                        Invalid email
                      </mat-error>
                    </mat-form-field>

                    <mat-checkbox *ngIf="field.type === 'checkbox'" [formControlName]="field.name">{{ field.label }}</mat-checkbox>
                    <div *ngIf="field.type === 'checkbox-group'" class="checkbox-group-container" [formGroupName]="field.name">
                      <label>{{ field.label }}</label>
                      <div fxLayout="row wrap" fxLayoutGap="16px">
                        <div *ngFor="let option of field.options" fxFlex="100" fxFlex.gt-sm="50" fxFlex.gt-md="25">
                          <mat-checkbox [formControlName]="option.value">{{ option.label }}</mat-checkbox>
                        </div>
                      </div>
                    </div>

                    <div *ngIf="field.type === 'signature'" class="signature-container">
                      <label>{{ field.label }}</label>
                      <div class="signature-pad-wrapper">
                        <app-signature-pad [formControlName]="field.name"></app-signature-pad>
                      </div>
                    </div>
                    <div *ngIf="field.type === 'static_text'" class="static-text-container">
                      <label>{{ field.label }}</label>
                      <app-markdown [markdown]="field.value"></app-markdown>
                    </div>
                    <div *ngIf="field.type === 'text' && field.readonly" class="readonly-text-container">
                      <label>{{ field.label }}</label>
                      <p>{{ field.value }}</p>
                    </div>
                    <div *ngIf="field.type === 'file'" class="file-input-container">
                      <label>{{ field.label }}</label>
                      <input type="file" (change)="onFileChange($event, field.name, sectionId, page.title)">
                    </div>
                  </div>
                </div>
              </ng-container>
            </div>

            <!-- Section Loading Spinner -->
            <div class="section-loading-container" *ngIf="loadingSection">
              <mat-spinner [diameter]="30" [color]="'accent'"></mat-spinner>
              <p>Saving section...</p>
            </div>

            <!-- NAVIGATION BUTTONS -->
            <div
              class="form-actions"
              fxLayout="row"
              fxLayoutAlign="end center"
              fxLayoutGap="8px"
              [class.hidden]="loadingSection"
            >
              <button
                mat-raised-button
                class="custom-button-accent"
                color="accent"
                *ngIf="i !== 0"
                matStepperPrevious
                [disabled]="loadingSection"
              >
                Back
              </button>
              <!--
                Pass the page index (i) so we know which step is being saved.
              -->
              <button
                mat-raised-button
                class="custom-button-primary"
                color="primary"
                (click)="savePage(page.title, stepper, i)"
                [disabled]="loadingSection"
              >
                Save & Next
              </button>
            </div>
          </form>
        </mat-step>
      </ng-container>

      <!-- ==================== Final "Review & Submit" Step ==================== -->
      <mat-step>
        <ng-template matStepLabel>Review & Submit</ng-template>
        <div fxLayout="row wrap" fxLayoutGap="16px">
          <div *ngFor="let page of layout.pages" fxFlex="100">
            <h3>{{ page.title }}</h3>
            <ng-container *ngFor="let sectionId of page.sections">
              <div *ngIf="formConfig[sectionId]">
                <h4>{{ formConfig[sectionId]?.metadata?.title }}</h4>
                <p>{{ formConfig[sectionId]?.metadata?.description }}</p>
              </div>

              <!-- Field Display (Signature, File, Static Text, etc.) -->
              <div *ngFor="let field of formConfig[sectionId]?.fields">
                <!--
                  1) If this is a checkbox-group, we list EVERY option
                     and show "Yes" or "No" depending on the user's selection.

                  2) Otherwise, we follow your existing pattern.
                -->
                <ng-container *ngIf="field.type === 'checkbox-group'; else normalField">
                  <!-- Title for the entire checkbox group -->
                  <p><strong>{{ field.label }}:</strong></p>

                  <!-- For each option in that checkbox-group -->
                  <div *ngFor="let option of field.options">
                    <!-- We check if user selected this option or not -->
                    <p style="margin-left: 1.5em;">
                      • {{ option.label }}:
                      <!-- isOptionChecked is a helper method in .ts (explained below) -->
                      {{ isOptionChecked(page.title, sectionId, field.name, option.value) ? 'Yes' : 'No' }}
                    </p>
                  </div>
                </ng-container>

                <!-- Normal fields fallback -->
                <ng-template #normalField>
                  <p>
                    <strong>{{ field.label }}:</strong>

                    <!-- If signature/file, display the image -->
                    <ng-container *ngIf="field.type === 'signature' || field.type === 'file'">
                      <img
                        *ngIf="formPages[page.title].get(sectionId)?.get(field.name)?.value"
                        [src]="sanitizeUrl(formPages[page.title].get(sectionId)?.get(field.name)?.value)"
                        alt="{{ field.label }}"
                        style="max-width: 100%; max-height: 200px; border: 1px solid #ccc; margin-top: 8px;"
                      />
                    </ng-container>

                    <!-- If static_text, use markdown -->
                    <ng-container *ngIf="field.type === 'static_text'">
                      <app-markdown
                        [markdown]="formPages[page.title].get(sectionId)?.get(field.name)?.value"
                      ></app-markdown>
                    </ng-container>

                    <!-- Anything else (text, select, date, etc.) -->
                    <ng-container
                      *ngIf="field.type !== 'static_text' && field.type !== 'signature' && field.type !== 'file'"
                    >
                      {{ formPages[page.title].get(sectionId)?.get(field.name)?.value || 'No data provided' }}
                    </ng-container>
                  </p>
                </ng-template>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- Final Submit Buttons -->
        <div class="form-actions" fxLayout="row" fxLayoutAlign="end center" fxLayoutGap="8px">
          <button mat-raised-button class="custom-button-primary" color="primary" (click)="onSubmit()">Submit</button>
          <button mat-raised-button class="custom-button-warn" color="warn" (click)="stepper.previous()">Back</button>
        </div>
      </mat-step>

    </mat-stepper>
  </ng-container>
</mat-card>
